/**
 * TankCleanContext — Jun 2026 display-only chip.
 *
 * Shows the tank type + quantity the customer reported in the booking
 * questions whenever a tank clean is recommended, so Admin / PWS
 * Partners can prepare a rough quote without digging.
 *
 * Two usage modes:
 *  • <TankCleanContext recommended context />        — data already in payload
 *  • <TankCleanContext reference="SCWT-XXX" fetch /> — self-fetching (admin booking detail)
 */
import { useEffect, useState } from "react";
import { Droplets } from "lucide-react";
import api from "@/lib/api";

export const TankCleanContext = ({ recommended, context, reference, fetch: selfFetch = false, testIdPrefix = "tank-clean-context" }) => {
  const [data, setData] = useState({ recommended: !!recommended, context: context || null });

  useEffect(() => {
    if (!selfFetch || !reference) return;
    api.get(`/admin/bookings/${reference}/tank-clean-context`)
      .then(({ data: d }) => setData({ recommended: !!d.tank_clean_recommended, context: d.tank_clean_context || null }))
      .catch(() => {});
  }, [selfFetch, reference]);

  useEffect(() => {
    if (!selfFetch) setData({ recommended: !!recommended, context: context || null });
  }, [recommended, context, selfFetch]);

  if (!data.recommended) return null;
  return (
    <div
      data-testid={testIdPrefix}
      className={`inline-flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold border ${
        data.context
          ? "bg-cyan-50 border-cyan-200 text-cyan-900"
          : "bg-amber-50 border-amber-200 text-amber-800"
      }`}
    >
      <Droplets className="w-3.5 h-3.5 flex-shrink-0 mt-[1px]" />
      {data.context
        ? <span>Tank clean quote context: <span data-testid={`${testIdPrefix}-value`}>{data.context}</span></span>
        : <span data-testid={`${testIdPrefix}-missing`}>Tank clean recommended — tank type/quantity not supplied</span>}
    </div>
  );
};
